import { Filter } from '../common/filter';
import { ticksToHours, WEEKDAYS } from '../common/datetime';

export class ChartBuilder {

  constructor(aggregate, filter, chartType, model) {
    this.aggregate = aggregate;
    this.filter = filter;
    this.chartType = chartType;
    this.model = model;
  }

  reset() {
    this.xData = [];
    this.yData = [];
    this.legendData = [];
    this.filterValues = [];
    this.filterYValues = [];
  }

  build() {
    this.reset();
    if (
      !this.aggregate ||
      !this.aggregate.filter ||
      !this.aggregate.filter.drilldown ||
      !this.aggregate.filter.drilldown.length ||
      !this.aggregate.cells ||
      !this.aggregate.cells.length
    ) {
      return;
    }
    let isSingleDim = this.filter.drilldown.length === 1;
    if (isSingleDim && !this.shouldSortDimension(
        this.filter.drilldown[0])) {
      this.legendData = [''];
      this.buildChartSingleDim();
    } else {
      this.buildChartData();
    }
  }

  /**
   * single dimension case
   */
  buildChartSingleDim() {
    let drill = this.filter.drilldown[0];
    this.filterValues = this.aggregate.cells.map((cell) => {
      return this.model.getCellFilter(drill, cell);
    });
    this.xData = this.filterValues.map((filter) => filter.label || '');
    this.yData = [
      {
        name: '',
        type: this.chartType,
        data: this.aggregate.cells.map(cell => this.getMeasureValue(cell)),
      },
    ];
  }

  buildChartData() {
    this.yKeysSet = {};
    let data = this.buildData();
    if (!data) {
      return;
    }

    this.buildXData(data);
    this.buildYData(data);
  }

  get isSingleDim() {
    return this.filter.drilldown.length === 1;
  }

  /**
   * xData builder
   * @returns {undefined}
   */
  buildXData(data) {
    let xDim = this.filter.drilldown[0];
    if (Filter.isTimeDimension(xDim)) {
      this.buildTimeLine(xDim, data);
    } else {
      this.xData = Object.keys(data);
      this.sortKeys(xDim, this.xData, data);
      this.filterValues = this.xData.map((key) => data[key].filter);
    }
  }

  /**
   * yData builder
   */
  buildYData(data) {
    if (this.isSingleDim) {
      this.legendData = [''];
      let values = this.xData.map((key) => (data[key] || {data: 0}).data);
      this.yData = [
        {
          name: '',
          type: this.chartType,
          data: values,
        },
      ];
    } else {
      let yDim = this.filter.drilldown[1];
      let yKeys = Object.keys(this.yKeysSet);
      this.sortKeys(yDim, yKeys, this.yKeysSet);
      this.legendData = yKeys;
      this.filterYValues = yKeys.map((key) => {
        return this.yKeysSet[key];
      });
      let yValues = this.xData.map((key) => {
        return (data[key] || {data: {}}).data;
      });
      this.yData = yKeys.map((yKey) => {
        let values = yValues.map(yValue => yValue[yKey] || 0);
        return {
          name: yKey,
          type: this.chartType,
          data: values,
        };
      });
    }
  }

  /**
   * aggregate data object
   * @returns {undefined | Object.<string, {value: *, data: Object.<string, *>}>}
   */
  buildData() {
    let data = {};

    let cells = this.aggregate.cells;
    for (let i = 0; i < cells.length; i++) {

      let cell = cells[i];
      let size = this.getMeasureValue(cell);
      let drills = this.filter.drilldown;
      let xFilter = this.model.getCellFilter(drills[0], cell);
      let xlabel = xFilter.label || xFilter.value;
      if (drills.length === 1) {
        data[xlabel] = {
          value: xFilter.value,
          data : size,
        };
        continue;
      }
      let yFilter = this.model.getCellFilter(drills[1], cell);
      let xFilterData = data[xlabel];
      if (!xFilterData) {
        data[xlabel] = xFilterData = {
          value : xFilter.value,
          data  : {},
          filter: xFilter,
        };
      }
      const ylabel = yFilter.label || yFilter.value;
      xFilterData.data[ylabel] = size +
        (xFilterData.data[ylabel] || 0);
      this.yKeysSet[ylabel] = yFilter;
    }

    return data;
  }

  /**
   * build linear time-line
   */
  buildTimeLine(dim, data) {
    let startDate = this.aggregate.filter.timeFrom;
    let endDate = this.aggregate.filter.timeTo;
    let count = endDate.getTime() - startDate.getTime();
    let timeGetter;
    let timeSetter;
    let dimParts = this.model.getDimensionParts(dim);
    let level = dimParts.hierarchy.levels.length;
    let quantity = dimParts.hierarchy.levels[level - 1];
    switch (quantity) {
      case 'minute':
        timeGetter = Date.prototype.getMinutes;
        timeSetter = Date.prototype.setMinutes;
        count = Math.floor(count / (60 * 1000));
        break;
      case 'hour':
        timeGetter = Date.prototype.getHours;
        timeSetter = Date.prototype.setHours;
        count = Math.floor(count / (60 * 60 * 1000));
        break;
      case 'day':
        timeGetter = Date.prototype.getDate;
        timeSetter = Date.prototype.setDate;
        count = Math.floor(count / (24 * 60 * 60 * 1000));
        break;
      case 'month':
        timeGetter = Date.prototype.getMonth;
        timeSetter = Date.prototype.setMonth;
        count = 12 * (endDate.getFullYear() - startDate.getFullYear()) +
          endDate.getMonth() - startDate.getMonth();
        break;
      case 'year':
        timeGetter = Date.prototype.getFullYear;
        timeSetter = Date.prototype.setFullYear;
        count = endDate.getYear() - startDate.getYear();
        break;
      default:
        console.error(
          quantity,
          'is unknown time line quantity, use day by default',
        );
        timeGetter = Date.prototype.getDate;
        timeSetter = Date.prototype.setDate;
        count = Math.floor(count / (24 * 60 * 60 * 1000));
        break;
    }
    count++;

    // trim left

    let date = new Date(startDate.getTime());
    while (!data[this.getTimeLabel(date, level)] && count) {
      timeSetter.call(date, timeGetter.call(date) + 1);
      --count;
    }

    // trim right

    let edate = new Date(endDate.getTime());
    while (count && !data[this.getTimeLabel(edate, level)]) {
      timeSetter.call(edate, timeGetter.call(edate) - 1);
      --count;
    }

    // build

    this.xData = new Array(count);
    for (let i = 0; i < count; ++i) {
      let label = this.xData[i] = this.getTimeLabel(date, level);
      this.filterValues[i] = {
        dimension: dim,
        label,
        value    : this.getTimeValue(date, level),
      };
      timeSetter.call(date, timeGetter.call(date) + 1);
    }
  }

  /**
   * convert date to string like '2016,10,11'
   * @param {Date} date
   * @param {number} level
   */
  getTimeValue(date, level) {
    return Filter.valueForTime(date, level, true);
  }

  /**
   * format time regarding dimension levels
   * @param {Date} date
   * @param {number} level
   */
  getTimeLabel(date, level) {
    return Filter.labelForTime(date, level);
  }

  /**
   * sort keys array
   * @param {string} dim dimension string
   * @param {string[]} keys keys array to sort
   * @param {Object.<string, {value: *}>} values
   */
  sortKeys(dim, keys, values) {
    if (Filter.isTimeDimension(dim)) {
      // Convert time values to number,
      // in order to optimize sorting
      keys.forEach(key => {
        let value = values[key].value;
        values[key].timeValue = value.split(',')
          .reduce((prev, cur) => 100 * prev + Number(cur), 0);
      });
    } else if (dim === 'agent_time@weekday:weekday') {
      keys.forEach((key) => {
        values[key].timeValue = WEEKDAYS.indexOf(key);
      });
    } else if (dim === 'agent_time@hour:hour') {
      keys.forEach((key) => {
        values[key].timeValue = Number(key);
      });
    }
    keys.sort(this.compareFilter(dim, values));
  }

  /**
   * returns comparator for sorting
   * @param {string} dim dimension string
   * @param {Object.<string, {value: *}>} values
   * @returns {function} | {undefined}
   */
  compareFilter(dim, values) {
    if (this.shouldSortDimension(dim)) {
      return compareFilterTime;
    }

    function compareFilterTime(a, b) {
      return values[a].timeValue - values[b].timeValue;
    }
  }

  shouldSortDimension(dim) {
    return Filter.isTimeDimension(dim) ||
      dim === 'agent_time@weekday:weekday' ||
      dim === 'agent_time@hour:hour';
  }

  getMeasureValue(cell) {
    let name = this.filter.measure;
    let val = typeof(cell) === 'object' ? cell && cell[name] || 0 : cell;
    if (name === 'activity_time_sum') {
      return val / ticksToHours;
    } else if (name === 'call_duration_sum') {
      return Math.floor(val / 60);
    } else {
      return val;
    } //bytesToHuman(val, true );
  }
}



// WEBPACK FOOTER //
// ./src/stat/chart-builder.js